import type { ReactNode } from "react"
import { Check } from "lucide-react"
import { BLOCKS, DIAGNOSTIC_DURATION } from "./content"
import type { BlockIndex } from "./flow"

/** Briques visuelles du diagnostic (styles : `diagnostic.css`). La molette est partagée : `@/components/shared/NumberWheel`. */

export function Eyebrow({ children }: { children: ReactNode }) {
  return <p className="zd-eyebrow">{children}</p>
}

export function ScreenTitle({ children, xl }: { children: ReactNode; xl?: boolean }) {
  return <h1 className={xl ? "zd-title zd-title-xl" : "zd-title"}>{children}</h1>
}

export function Lead({ children }: { children: ReactNode }) {
  return <p className="zd-lead">{children}</p>
}

// ── En-tête : retour, durée, bloc en cours, barre en trois segments ────────
export function DiagnosticHeader({
  block, progress, onBack,
}: {
  block: BlockIndex
  progress: [number, number, number]
  onBack: () => void
}) {
  return (
    <header className="zd-header">
      <div className="zd-header-row">
        <button type="button" className="zd-back" onClick={onBack}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
          </svg>
          Retour
        </button>
        <span className="zd-duration">{DIAGNOSTIC_DURATION.toUpperCase()}</span>
      </div>
      <p className="zd-eyebrow">Bloc {block + 1}/3 — {BLOCKS[block]}</p>
      <div
        className="zd-segments"
        role="progressbar"
        aria-label="Avancement du diagnostic"
        aria-valuemin={0}
        aria-valuemax={3}
        aria-valuenow={progress.reduce((a, b) => a + b, 0)}
        aria-valuetext={`Bloc ${block + 1} sur 3 : ${BLOCKS[block]}`}
      >
        {BLOCKS.map((label, i) => (
          <div key={label} className="zd-segment">
            <div className="zd-segment-track">
              <div className="zd-segment-fill" style={{ width: `${Math.round(progress[i] * 100)}%` }} />
            </div>
            <span className="zd-segment-label" data-current={i === block}>{label}</span>
          </div>
        ))}
      </div>
    </header>
  )
}

// ── Carte de choix ─────────────────────────────────────────────────────────
export function Choice({
  selected, onClick, disabled, className, children,
}: {
  selected: boolean
  onClick: () => void
  disabled?: boolean
  className?: string
  children: ReactNode
}) {
  return (
    <button
      type="button"
      className={className ? `zd-choice ${className}` : "zd-choice"}
      aria-pressed={selected}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  )
}

export function CheckMark() {
  return (
    <span className="zd-check" aria-hidden="true">
      <Check className="w-4 h-4" strokeWidth={3} />
    </span>
  )
}
